import { useEffect } from 'react';
import { usePageTitleSetter } from '@/app/shells/PublicShell';

/**
 * Names the current public page in `document.title`, as "<page> — AKIS".
 *
 * PublicShell sets the zone's app title as a floor and its own comment invites a page to set its
 * own after it; until this hook, none did. Every route in the public zone — the calendar, each
 * event, each news article, the map, the advisor registry — answered with the same string, so a
 * visitor with nine events open saw nine identical tabs, a bookmark said nothing about what was
 * bookmarked, and a screen reader announced the same sentence on arrival at every page. That last
 * one is WCAG 2.4.2 "Page Titled", a level A criterion on a deliverable contracted at AA.
 *
 * The page's own name comes FIRST because a tab, a bookmark and a search result all truncate from
 * the right, and the half that identifies the page is the half that has to survive.
 *
 * Pass `null` while the page is still loading its subject: the zone title is a better placeholder
 * than a title built from a name that has not arrived, and the hook re-runs when it does. Callers
 * pass the same string their `<h1>` renders — WCAG asks the title to describe the page, and the
 * heading is already that description, so keeping them the same value keeps them from drifting.
 */
export function usePublicPageTitle(page: string | null | undefined): void {
  const setPageTitle = usePageTitleSetter();
  useEffect(() => {
    setPageTitle(page ?? null);
    // Cleared on the way out so the next route does not inherit this page's name if it sets none.
    return () => setPageTitle(null);
  }, [page, setPageTitle]);
}
